import React, { useMemo, useRef, useEffect, memo } from 'react';
import { format, addMinutes } from 'date-fns';
import { resolveItemLabelStyle } from '../utils/pastelFromSeed';
import { useCalendarDragDrop } from '../drag-drop/CalendarDragDropContext';
import '../styles/TeamLabelCalendar.css';

const HIDDEN_CALENDAR_PILL_LABELS = new Set([
  'completed',
  'confirmed',
  'confirm',
  'unpaid',
]);

function shouldShowCalendarPill(value) {
  if (!value) return false;
  return !HIDDEN_CALENDAR_PILL_LABELS.has(String(value).trim().toLowerCase());
}

function metaParts(apt) {
  if (apt.type === 'meeting') {
    const parts = [];
    if (apt.meetingType === 'group') parts.push('Group');
    else if (apt.meetingType) parts.push('1:1');
    if (shouldShowCalendarPill(apt.status)) parts.push(String(apt.status));
    return parts;
  }
  const parts = [];
  if (apt.serviceName) parts.push(apt.serviceName);
  if (shouldShowCalendarPill(apt.status)) parts.push(String(apt.status));
  if (shouldShowCalendarPill(apt.paymentStatus)) parts.push(String(apt.paymentStatus));
  return parts;
}

function timeRangeLabel(apt) {
  const start = apt.dateTime;
  if (!start || isNaN(start.getTime())) return null;
  let end = apt.endTime;
  if (!end || isNaN(end.getTime())) {
    const dur = typeof apt.duration === 'number' ? apt.duration : 60;
    end = addMinutes(start, dur);
  }
  const startStr = format(start, 'h:mm a');
  if (!end || end <= start) return startStr;
  return `${startStr} – ${format(end, 'h:mm a')}`;
}

function resolveHeightPx(style) {
  const h = style?.height;
  if (typeof h === 'number' && !Number.isNaN(h)) return h;
  if (typeof h === 'string') {
    const n = parseFloat(h);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function AppointmentLabelCard({
  appointment,
  style,
  onClick,
  sourceColumnId,
}) {
  const cardRef = useRef(null);
  const suppressClickRef = useRef(false);
  const dragStartedRef = useRef(false);
  const { canDragGrid, beginDrag, dragState } = useCalendarDragDrop() || {};
  const colors = useMemo(
    () => resolveItemLabelStyle(appointment),
    [appointment]
  );

  const isDragging =
    dragState?.item?.id === appointment.id &&
    (appointment.type ? dragState.item.type === appointment.type : true);

  useEffect(() => {
    if (isDragging) {
      dragStartedRef.current = true;
      return;
    }
    if (dragStartedRef.current) {
      suppressClickRef.current = true;
      dragStartedRef.current = false;
    }
  }, [isDragging]);

  const title =
    appointment.type === 'meeting'
      ? appointment.title || appointment.clientName || 'Meeting'
      : appointment.clientName || 'Appointment';

  const heightPx = resolveHeightPx(style);
  const compact = heightPx > 0 && heightPx < 44;
  const showTime = !compact && heightPx >= 56;
  const pills = metaParts(appointment);
  const showPills = !compact && pills.length > 0;
  const timeLabel = timeRangeLabel(appointment);

  const handlePointerDown = (event) => {
    if (!canDragGrid || appointment.type === 'meeting' || event.button !== 0) return;
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    suppressClickRef.current = false;
    beginDrag?.(
      { ...appointment, __dragWidth: rect.width },
      sourceColumnId,
      event,
      rect
    );
  };

  const handleClick = () => {
    if (suppressClickRef.current || isDragging) {
      suppressClickRef.current = false;
      return;
    }
    onClick?.(appointment);
  };

  const typeClass =
    appointment.type === 'meeting' ? ' team-cal-v3__label-card--meeting' : '';
  const stackedClass =
    typeof style?.zIndex === 'number' && style.zIndex > 1
      ? ' team-cal-v3__label-card--stacked'
      : '';

  return (
    <button
      ref={cardRef}
      type="button"
      className={`team-cal-v3__label-card${typeClass}${stackedClass}${compact ? ' team-cal-v3__label-card--compact' : ''}${canDragGrid && appointment.type !== 'meeting' ? ' team-cal-v3__label-card--draggable' : ''}${isDragging ? ' team-cal-v3__label-card--dragging' : ''}`}
      style={{ ...colors, ...style }}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      <span className="team-cal-v3__label-card-accent" aria-hidden />
      <span className="team-cal-v3__label-card-inner">
        {showTime && timeLabel ? (
          <span className="team-cal-v3__label-card-time">{timeLabel}</span>
        ) : null}
        <span className="team-cal-v3__label-card-title">{title}</span>
        {showPills ? (
          <span className="team-cal-v3__label-card-pills">
            {pills.map((part) => (
              <span key={part} className="team-cal-v3__label-card-pill">
                {part}
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </button>
  );
}

export default memo(AppointmentLabelCard, (prev, next) => {
  if (prev.appointment?.id !== next.appointment?.id) return false;
  if (prev.appointment?.type !== next.appointment?.type) return false;
  if (prev.sourceColumnId !== next.sourceColumnId) return false;
  if (prev.onClick !== next.onClick) return false;
  const a = prev.style || {};
  const b = next.style || {};
  return (
    a.top === b.top &&
    a.height === b.height &&
    a.left === b.left &&
    a.width === b.width &&
    a.zIndex === b.zIndex
  );
});
